/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useContext } from 'react';
import styled from 'styled-components';
import MapSizeController, { MapSizesEnum } from './MapSizeController';
import ToggleTile, { ToggleTileStateEnum } from '../../materialui_wrappers/ToggleTile';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import { OverlaysContextsEnum } from '../../../store/maps/reducer';
import OverlaysWrapper from '../map_overlays/OverlaysWrapper';
import { PanelLabel } from '../map_overlays/PanelLabel';

import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectMapSettingsPanelShown,
  selectOverlaysCurrentContext,
  selectMapSettingsFtvMapSize,
  selectMapSettingsLabelsTileState,
  selectMapSettingsSatViewTileState,
  selectMapSettingsPrecipRadarTileState,
} from '../../../store';

const MAP_SETTINGS_PANEL_TOP_PX = 107;

const TilesGroup = styled.div`
  margin: 13px 0 0 15px;
`;

const StyledMapSize = styled.div`
  margin: 25px 0 64px 0;
`;

export function MapSettingsPanel() {
  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const {
    setMapSettingsFTVMapSize,
    setMapSettingsLabels,
    setMapSettingsSatView,
    setMapSettingsPrecipRadar,
    hideMapSettingsPanel,
  } = mapActionsContext.mapActions;

  const mapSettingsPanelShown = useSelectorWithMapId(selectMapSettingsPanelShown);
  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext);
  const mapSettingsFtvMapSize = useSelectorWithMapId(selectMapSettingsFtvMapSize);
  const mapSettingsLabelsTileState = useSelectorWithMapId(selectMapSettingsLabelsTileState);
  const mapSettingsSatViewTileState = useSelectorWithMapId(selectMapSettingsSatViewTileState);
  const mapSettingsPrecipRadarTileState = useSelectorWithMapId(selectMapSettingsPrecipRadarTileState);

  const top = MAP_SETTINGS_PANEL_TOP_PX + 'px';
  return (
    <OverlaysWrapper
      headerTitle="Map Settings"
      queryParamsToNotHidePanel={['#map_settings_panel_btn_container']}
      shown={mapSettingsPanelShown}
      hidePanel={() => {
        dispatch(hideMapSettingsPanel());
      }}
      top={top}
      width="224px"
    >
      {/* Map Size is only visible in FTV */}
      {overlaysCurrentContext === OverlaysContextsEnum.FTV ||
      overlaysCurrentContext === OverlaysContextsEnum.FTV_EXPORT ? (
        <StyledMapSize>
          <PanelLabel>MAP SIZE</PanelLabel>

          <MapSizeController
            selectedValue={mapSettingsFtvMapSize}
            setSelectedValue={(mapSize: MapSizesEnum, mapHeight: number) => {
              dispatch(setMapSettingsFTVMapSize(mapSize, mapHeight));
            }}
          />
        </StyledMapSize>
      ) : null}

      <PanelLabel>DISPLAY PREFERENCES</PanelLabel>

      <TilesGroup>
        <ToggleTile
          id="map_settings_sat_view"
          icon={<PublicOutlinedIcon />}
          caption="Satellite View"
          tileState={mapSettingsSatViewTileState}
          setTileState={(newTileState: ToggleTileStateEnum) => {
            dispatch(setMapSettingsSatView(newTileState));
          }}
        />

        <ToggleTile
          id="map_settings_labels"
          icon={<LocationOnOutlinedIcon />}
          caption="Labels"
          tileState={mapSettingsLabelsTileState}
          setTileState={(newTileState: ToggleTileStateEnum) => {
            dispatch(setMapSettingsLabels(newTileState));
          }}
        />

        <ToggleTile
          id="map_settings_precip_radar"
          icon={<CloudOutlinedIcon />}
          caption="Precipitation Radar"
          tileState={mapSettingsPrecipRadarTileState}
          setTileState={(newTileState: ToggleTileStateEnum) => {
            dispatch(setMapSettingsPrecipRadar(newTileState));
          }}
        />
      </TilesGroup>
    </OverlaysWrapper>
  );
}

export default MapSettingsPanel;
